import { Injectable } from '@angular/core';
import { XAPIDataGenerator } from 'libs-frontend-xapi/dist/js/xapidatagenerator.js';
import { XAPIHelperService } from './xAPI-helper.service';
import { nanoid } from 'nanoid';
import { HttpClient } from '@angular/common/http';
import { ApiGatewayHelperService } from 'modules/app/services/apigateway-helper.service';
import { DesktopEBookVMService } from 'modules/desktop/services/desktop-ebook-vm.service';

@Injectable()
export class LastPageDataService {

    callWaiting = false;
    timer: any;
    xAPIPostDelay = 5000;

    _lastPageData: any;
    _dataActions = {
        CREATE: "create",
        DELETE: "delete",
    }

    _pageLabelCacheMap = {};
    _lastPageKeyLocalStorage = 'lastPageDataInLocalStorage';
    _uniqueNanoIdMap = {};

    // Do your operations
    constructor(
        private xAPIHelperService: XAPIHelperService,
        private apiGatewayHelperService: ApiGatewayHelperService,
        private http: HttpClient,
        private desktopEBookVMService: DesktopEBookVMService
    ) { }

    /* Using note data and creating custom statement to be posted to the xAPI*/
    createCustomStatement(action?) {
        let userData;
        //sending xAPI to delete current note
        let localLastPageData = this._getLastPageDataFromLocalStorage();
        userData = localLastPageData.user_data;

        let options = {
            'user_data': userData
        }

        return {
            id: "" + userData.documentid,
            statement: XAPIDataGenerator.generateCustomStatement("" + userData.documentid, 'settings-updated', options),
            userData: userData
        };
    }

    /**
     * Function to make postStatments call
     * @param customStatement : response received from xAPI generateCustomStatement call
     */
    async postStatmentToXAPI({ id, statement, userData }) {

        localStorage.removeItem(this._lastPageKeyLocalStorage);
        this.callWaiting = false;

        if (this.desktopEBookVMService.getActiveVM()) {
            this.desktopEBookVMService.addNewDirtyRecord(DesktopEBookVMService.USER_DATA_TYPE.PREFERENCES.LAST_PAGE, { id, statement, userData });
        } else {
            this.xAPIHelperService.postStatmentToXAPI(id, statement);
        }

    }

    /* Saving note data */
    _saveLastPageData(bookCode, userId, spaceId, pageLabel) {
        let action = this._dataActions.CREATE
        if (!this._pageLabelCacheMap[bookCode + '-' + userId]) {
            action = this._dataActions.DELETE;
        }

        let uniqueID = this._getUniqueId(bookCode, userId);


        // need folder id
        // need type

        // bookcode/note/p_no
        // bookcode/annotation/p_no
        // bookcode/verb/lastPage
        this._lastPageData = {
            "identifier": uniqueID,
            "user_data": {
                "documentid": 'lastVisitedPage',
                "folderid": 'settings/product/'+ bookCode + '/prefs',
                "action": action,
                "type": 'settings-updated',
                "payload": JSON.stringify({
                    "data": {
                        lastPageLabel: pageLabel
                    }
                })
            }
        }

        let localLastPageData = this._getLastPageDataFromLocalStorage();
        if (action === this._dataActions.CREATE) {
            if (this.callWaiting
                && localLastPageData
                && (localLastPageData.identifier !== this._lastPageData.identifier)
            ) {
                this.timer && clearTimeout(this.timer);
                this.postStatmentToXAPI(this.createCustomStatement(this._dataActions.CREATE));
            }
            localStorage.setItem(this._lastPageKeyLocalStorage, JSON.stringify(this._lastPageData));
            this._postLastPageData();
        }
    }

    /* delaying xAPI call for delayed seconds */
    _postLastPageData() {
        if (!this.callWaiting) {
            this.callWaiting = true;
            this.timer = setTimeout(() => {
                this.postStatmentToXAPI(this.createCustomStatement(this._dataActions.CREATE));
            }, this.xAPIPostDelay)
        }
    }

    /* Fetching notes data from local storage */
    _getLastPageDataFromLocalStorage() {
        return JSON.parse(localStorage.getItem(this._lastPageKeyLocalStorage));
    }

    checkPendingDataInLocalStorage() {
        let data = this._getLastPageDataFromLocalStorage();
        return data;
    }

    postPendingLastPageToXAPI() {
        let hasLocalData = this._getLastPageDataFromLocalStorage();
        /* triggering xAPI to post immediately*/
        this.timer && clearTimeout(this.timer)
        hasLocalData && this.postStatmentToXAPI(this.createCustomStatement());
    }

    async getLastPageData(bookCode, userId) {
        // check if data is present in cache. if present return
        // else api call

        if (!bookCode || !userId) {
            return;
        }

        let pageLabel = this._pageLabelCacheMap[bookCode + '-' + userId];

        if (!pageLabel) {
            pageLabel = await this._getLastPageFromServer(bookCode, userId);
        }

        return pageLabel;

    }

    setLastPageData(bookCode, userId, spaceId, pageLabel) {

        // set data in cache.
        // then send data to server via xapi

        // if new label is same as current label, then return
        if (this._pageLabelCacheMap[bookCode + '-' + userId] && this._pageLabelCacheMap[bookCode + '-' + userId] == pageLabel) {
            return;
        }

        this._pageLabelCacheMap[bookCode + '-' + userId] = pageLabel;

        // send page label to server using xapi.
        this._saveLastPageData(bookCode, userId, spaceId, pageLabel);

    }

    async _getLastPageFromServer(bookCode, userId) {

        try {

            let lastPageResponse: any = await this.http.get(this.apiGatewayHelperService.getApiGatewayPrefixUrl() + 'getLastPage/' + bookCode + '/' + userId).toPromise();
            
            if (lastPageResponse && lastPageResponse.entities && lastPageResponse.entities.length > 0) {
                this._pageLabelCacheMap[bookCode + '-' + userId] = JSON.parse(lastPageResponse.entities[0].data).data.lastPageLabel;
                this._setUniqueId(bookCode, userId, lastPageResponse.entities[0].documentid);
                return JSON.parse(lastPageResponse.entities[0].data).data.lastPageLabel
            }
            return undefined;

        }
        catch(error) {
            console.log(error);
            return undefined;
        }
        
    }

    _setUniqueId(bookCode, userId, id) {
        this._uniqueNanoIdMap[bookCode + '-' + userId] = id;
    }

    _getUniqueId(bookCode, userId) {

        let id = this._uniqueNanoIdMap[bookCode + '-' + userId];
        if (!id) {
            id = nanoid(16);
            this._uniqueNanoIdMap[bookCode + '-' + userId] = id;
        }
        return id;

    }

    setLastPageDataCache(bookCode, userId, spaceId, pageLabel, entitiesObject) {
        // set data recieved from server in cache.
        this._pageLabelCacheMap[bookCode + '-' + userId] = pageLabel;
        
        if(entitiesObject) {
            this._setUniqueId(bookCode, userId, entitiesObject.documentid);
        }

    }

}